import { useState, useEffect, useCallback } from "react";
import { Link, useHistory } from "react-router-dom";
import Alert from "@mui/material/Alert";
import { supabase } from "utils/supabase";
import { useAuth } from "utils/AuthContext.js";
import enarmApexLogo from "assets/img/enarm-apex-logo.png";

export default function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [isRedirecting, setIsRedirecting] = useState(false)
  const history = useHistory()
  const { loggedIn, loading } = useAuth()
  const showBlockingLoader = loading || isRedirecting || loggedIn

  const pushWithLoader = useCallback(async (path) => {
    setIsRedirecting(true)
    // Allow one short frame window so the loader paints before route change.
    await new Promise((resolve) => setTimeout(resolve, 180))
    history.push(path)
  }, [history])

  useEffect(() => {
    const rememberedEmail = localStorage.getItem('rememberedEmail')

    if (rememberedEmail) {
      setEmail(rememberedEmail)
      setRememberMe(true)
    }

    localStorage.removeItem('rememberedPassword')
  }, [])

    async function handleLogin(e) {
      e.preventDefault()
      setErrorMessage('')
      setIsRedirecting(true)

      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (error) {
        if (error.message === 'missing email or phone') {
          setErrorMessage('Por favor ingresa tu correo electrónico.')
        } else if (error.message === 'missing password') {
          setErrorMessage('Por favor ingresa tu contraseña.')
        } else if (error.message === 'Invalid login credentials') {
          setErrorMessage('Correo electrónico o contraseña incorrectos.')
        } else if (error.message === 'Email not confirmed') {
          setErrorMessage('Por favor confirma tu correo electrónico antes de iniciar sesión.')
        } else {
          setErrorMessage('Ocurrió un error al iniciar sesión. Por favor intenta de nuevo.')
        }
        console.error(error)
        setIsRedirecting(false)
        return
      }

      if (rememberMe) {
        localStorage.setItem('rememberedEmail', email)
      } else {
        localStorage.removeItem('rememberedEmail')
      }

      // redirigir al dashboard
      await redirectAfterLogin()
    }

  const redirectAfterLogin = useCallback(async () => {
    const { data: billing, error } = await supabase.rpc("get_billing_status");
    if (error) {
      console.error("Error revisando suscripción:", error);
      await pushWithLoader("/auth/pagos");
      return;
    }

    if (billing?.has_pro_access) {
      await pushWithLoader("/admin/dashboard");
      return;
    }

    await pushWithLoader("/auth/pagos");
  }, [pushWithLoader]);

  useEffect(() => {
    if (loggedIn && !isRedirecting) {
      setIsRedirecting(true)
      redirectAfterLogin();
    }
  }, [loggedIn, isRedirecting, redirectAfterLogin])

  if (showBlockingLoader) {
    return (
      <div className="container mx-auto px-4 h-full">
        <div className="flex content-start items-start justify-center h-full">
          <div className="w-full lg:w-4/12 px-4 -mt-20">
            <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-lg bg-blueGray-200 border-0">
              <div className="rounded-t mb-0 px-6 py-6">
                <div className="text-center mb-3">
                  <img
                    src={enarmApexLogo}
                    alt="ENARM Apex Logo"
                    className="w-32 mx-auto"
                    style={{ width: "20rem" }}
                  />
                  <br />
                  <h6 className="text-blueGray-500 text-lg font-bold">
                    Cargando...
                  </h6>
                </div>
              </div>
              <div className="flex-auto px-4 lg:px-10 py-10 pt-0">
                <div className="flex flex-col items-center justify-center py-6">
                  <div
                    className="w-12 h-12 border-4 border-blueGray-200 rounded-full animate-spin"
                    style={{ borderTopColor: "#475569" }}
                  />
                  <p className="mt-4 text-sm font-semibold text-blueGray-600">Cargando informacion...</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="container mx-auto px-4 h-full">
        <div className="flex content-start items-start justify-center h-full">
          <div className="w-full lg:w-4/12 px-4 -mt-20">
            <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-lg bg-blueGray-200 border-0">
              <div className="rounded-t mb-0 px-6 py-6">
                <div className="text-center mb-3">
                  <img
                    src={enarmApexLogo}
                    alt="ENARM Apex Logo"
                    className="w-32 mx-auto"
                    style={{ width: "20rem" }}
                  />
                  <br />
                  <h6 className="text-blueGray-500 text-lg font-bold">
                    Inicia sesión
                  </h6>
                </div>
              </div>
              <div className="flex-auto px-4 lg:px-10 py-10 pt-0">
                <form onSubmit={handleLogin}>
                  {errorMessage && (
                    <Alert severity="error" className="mb-4">
                      {errorMessage}
                    </Alert>
                  )}

                  <div className="relative w-full mb-3">
                    <label
                      className="block uppercase text-blueGray-600 text-xs font-bold mb-2"
                      htmlFor="grid-password"
                    >
                      Email
                    </label>
                    <input
                      type="email"
                      className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                      placeholder="Email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>

                  <div className="relative w-full mb-3">
                    <label
                      className="block uppercase text-blueGray-600 text-xs font-bold mb-2"
                      htmlFor="grid-password"
                    >
                      Contraseña
                    </label>
                    <input
                      type="password"
                      className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
                      placeholder="Contraseña"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="inline-flex items-center cursor-pointer">
                      <input
                        id="customCheckLogin"
                        type="checkbox"
                        className="form-checkbox border-0 rounded text-blueGray-700 ml-1 w-5 h-5 ease-linear transition-all duration-150"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                      />
                      <span className="ml-2 text-sm font-semibold text-blueGray-600">
                        Recuerda mi cuenta
                      </span>
                    </label>
                  </div>

                  <div className="text-center mt-6">
                    <button
                      className="bg-blueGray-800 text-white active:bg-blueGray-600 text-sm font-bold uppercase px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 w-full ease-linear transition-all duration-150 disabled:opacity-70"
                      type="submit"
                      disabled={isRedirecting || loading}
                    >
                      {isRedirecting || loading ? "Cargando..." : "Entrar"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
            <div className="flex flex-wrap mt-6 relative">
              <div className="w-1/2">
                <Link to="/auth/recuperar-contrasena" className="text-blueGray-200">
                  <small>Olvidaste tu contraseña?</small>
                </Link>
              </div>
              <div className="w-1/2 text-right">
                <Link to="/auth/register" className="text-blueGray-200">
                  <small>Crear cuenta</small>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
